import Vue from "vue";
import UserTypeSelector from "../../components/UserTypeSelector.vue";
import { SelectOption, UserType, AccountInfo } from "../../types";
import qs from "qs";
import { USER_GUARDIAN, USER_TUTOR } from "../../constants";
import { displayableUserType } from "../../utils";
import { PageComponent as EditAccount } from "../account/page_edit";
import isLength from "validator/lib/isLength";
import isEmail from "validator/lib/isEmail";
import isEmpty from "validator/lib/isEmpty";
import isMobilePhone from "validator/lib/isMobilePhone";

const referralSources = [
  "Parent",
  "Tutor",
  "Facebook",
  "LinkedIn",
  "Other Social Media",
  "Other"
];

export const PageComponent = EditAccount.extend({
  props: {
    initialReferralSource: {
      type: String,
      required: false
    }
  },

  data() {
    // try to parse a valid user type from the querystring to pre-select
    let userType =
      qs.parse(window.location.search.substr(1) || "")?.type || null;

    if (typeof userType !== "string") {
      userType = null;
    } else {
      userType = userType.toLowerCase();

      if (![USER_GUARDIAN, USER_TUTOR].includes(userType as UserType)) {
        userType = null;
      }
    }

    return {
      agreedToTerms: false,
      userType,
      recaptchaToken: null as null | string,
      userTypeConfirmed: userType !== null,
      referralSource: this.initialReferralSource as string
    };
  },

  computed: {
    referralSourcesAsSelectOptions(): SelectOption[] {
      return referralSources.map(source => ({
        key: source,
        value: source,
        label: source
      }));
    },

    isTutor(): boolean {
      return this.userType === USER_TUTOR;
    }
  },

  methods: {
    displayableUserType,

    updateReferralSource(value: string): void {
      this.referralSource = value;
    },

    toggleUserType(userType: string): void {
      if (this.userType === userType) {
        this.userType = null;
      } else {
        this.userType = userType;
      }
    },

    doRecaptchaAndSubmit(): boolean {
      if (this.recaptchaToken || !window.grecaptcha) {
        // a token has been set OR the grecaptcha library has not been loaded
        return true;
      }

      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(process.env.VUE_APP_RECAPTCHA_SITE_KEY, { action: "submit" })
          .then((token: string) => {
            this.recaptchaToken = token;
            this.$nextTick(() => (this.$refs.form as HTMLFormElement).submit());
          });
      });

      return false;
    },

    async validate() {
      this.clearErrors();

      if (!this.agreedToTerms) {
        this.applyError(
          "i_agree",
          "You must agree to the terms above to continue."
        );
      }

      ([
        "first_name",
        "last_name",
        "email",
        "password",
        "phone",
        "zip_code"
      ] as Array<keyof Omit<AccountInfo, "password_confirmation">>).forEach(
        field => {
          if (isEmpty(this.account[field] || "")) {
            this.applyError(field, "This field is required.");
          }
        }
      );

      if (!this.hasError("email") && !isEmail(this.account.email || "")) {
        this.applyError("email", "A valid email address is required.");
      }

      if (
        !this.hasError("password") &&
        !isLength(this.account.password || "", { min: 8 })
      ) {
        this.applyError(
          "password",
          "The password must be at least 8 characters."
        );
      }

      if (
        !this.hasError("zip_code") &&
        !(await this.isZipCodeValid(this.account.zip_code || ""))
      ) {
        this.applyError("zip_code", "The zip code is invalid");
      }

      if (
        !this.hasError("phone") &&
        !isMobilePhone(this.account.phone || "", "en-US")
      ) {
        this.applyError("phone", "A valid phone number is required");
      }

      return !this.hasErrors();
    },

    async validateAndSubmit(event?: Event): Promise<void> {
      if (this.submitting) {
        // let the form submission go through.
        return;
      }

      event?.preventDefault();

      this.submitting = true;

      if (!this.doRecaptchaAndSubmit()) {
        // recaptcha failed
        this.submitting = false;
        return;
      }

      if (!(await this.validate())) {
        // validation failed
        this.submitting = false;
        return;
      }

      (this.$refs.form as HTMLFormElement).submit();
    }
  },

  components: {
    UserTypeSelector
  }
});
